import { useCallback, useEffect, useState } from 'react';
import { isString } from 'src/utils/typeguards/isString';
import { isUndefined } from 'src/utils/typeguards/isUndefined';
import {
    deleteSessionStorageItem,
    getParsedSessionStorageItem,
    getRawSessionStorageItem,
    setRawSessionStorageItem,
    setSessionStorageItem,
} from '../session';

type UseSessionStoragePersistedValueReturnValue<TValueType> = [
    TValueType,
    (updatedValue: TValueType) => void,
    boolean,
    () => void
];

/**
 * Behaves similarly to React's useState hook, but persists data to session storage rather than in-memory.
 *
 * @param key the session storage key to persist and hydrate from
 * @param defaultValue the default value for the hook, similar to React's useState default value.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSessionStoragePersistedState<TValueType = any>(
    key: string,
    defaultValue?: TValueType
): UseSessionStoragePersistedValueReturnValue<TValueType | undefined> {
    const [reactValue, setReactValue] = useState<TValueType | undefined>(defaultValue);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const persistedValue = isString(defaultValue)
            ? getRawSessionStorageItem(key)
            : getParsedSessionStorageItem<typeof defaultValue>(key);
        if (!isUndefined(persistedValue)) {
            // Cast because the check done during the session storage rehydration guarantees
            // it will be TValueType or undefined
            setReactValue(persistedValue as TValueType | undefined);
        }
        setIsLoaded(true);
    }, [key, defaultValue]);

    const updateReactValue = useCallback(
        (value: typeof reactValue) => {
            if (isString(value)) {
                setRawSessionStorageItem(key, value as string);
            } else {
                setSessionStorageItem<typeof reactValue>(key, value);
            }
            setReactValue(value);
        },
        [key]
    );

    const clearValue = useCallback(() => {
        deleteSessionStorageItem(key);
        setReactValue(undefined);
    }, [key]);

    const result: UseSessionStoragePersistedValueReturnValue<typeof reactValue> = [
        reactValue,
        updateReactValue,
        isLoaded,
        clearValue,
    ];
    return result;
}
